import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
} from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import {
  ThemeContext,
  useDimensions,
} from '../../context/ThemeContextManagement';
import useHlsVideo, { isM3U8 } from '../../utils/useHlsVideo';
import CustomImage from '../custom-image/CustomImage.jsx';

// Low-buffer config tuned for a short, silently looping cinemagraph.
//
// The cap settings are load-bearing: without them ABR climbs to whatever the
// connection allows, and a 412px phone was pulling Bunny's 1440p rendition
// (6.6MB) for a hero it paints at 412px wide. `capLevelToPlayerSize` picks the
// smallest rendition that still covers the element's rendered box, re-checked
// once a second, so the hero's loader-intro scale and parallax settle out on
// their own. `maxDevicePixelRatio: 1.5` stops a 3x phone screen from asking for
// a 1200px-wide rendition of what is a silent decorative background — full DPR
// sharpness isn't worth 5MB here.
const CINEMAGRAPH_HLS_CONFIG = {
  maxBufferLength: 3,
  maxMaxBufferLength: 5,
  backBufferLength: 2,
  autoStartLoad: true,
  startLevel: 0,
  lowLatencyMode: true,
  testBitrate: false,
  enableWorker: true,
  progressive: true,
  capLevelToPlayerSize: true,
  capLevelOnFPSDrop: true,
  maxDevicePixelRatio: 1.5,
  // Fetch the first fragment as soon as the level playlist is parsed, without
  // waiting for the media element to be attached and ready. Shaves one
  // round-trip off the time-to-first-frame, which is the whole point of the
  // poster/video handoff below.
  startFragPrefetch: true,
};

function supportsFrameCallback(video) {
  return typeof video?.requestVideoFrameCallback === 'function';
}

export default function Cinemagraph({
  media,
  mediaMobile = false,
  poster = false,
  fill = false,
  forceHeight = false,
  className = 'MediaItem',
  forceLoad = false,
  parallax = false,
  parentRef = false,
  // Forwarded to the poster's CustomImage. Pass 'high' when this cinemagraph is
  // the first block on the page — the poster is the LCP element (the HLS
  // manifest itself can't be fetchpriority'd), so it should outrank everything
  // else in the request queue. See Hero.jsx for the same pattern.
  fetchPriority,
}) {
  const [loadMedia, setLoadMedia] = useState(forceLoad);
  const [loaded, setLoaded] = useState(false);
  const [playMedia, setPlayMedia] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const boxRef = useRef(null);
  const mediaRef = useRef(null);
  const loadObserverRef = useRef(null);
  const playObserverRef = useRef(null);

  const { touchFired, touch } = useContext(ThemeContext);
  const dimensions = useDimensions();

  const activeSrc =
    mediaMobile && dimensions.screenWidth < 768 ? mediaMobile.url : media?.url;

  const activeIsM3U8 = isM3U8(activeSrc);

  useHlsVideo({
    videoRef: mediaRef,
    src: activeIsM3U8 ? activeSrc : null,
    enabled: loadMedia && activeIsM3U8,
    config: CINEMAGRAPH_HLS_CONFIG,
  });

  // ✅ Manual loop ONLY for HLS
  //
  // This used to re-pin `hls.currentLevel` to the last played level on every
  // loop. That setter puts hls.js into *manual* level mode, which switches off
  // `capLevelToPlayerSize` — the cap that keeps this from streaming a 1440p
  // rendition into a phone-sized box. Seeking back to 0 doesn't reset ABR, so
  // the pin bought nothing; staying in auto mode keeps the cap live across
  // resizes and orientation changes.
  useEffect(() => {
    const video = mediaRef.current;
    if (!video || !activeIsM3U8) return;

    const handleEnded = () => {
      video.currentTime = 0;

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    };

    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, [activeIsM3U8]);

  const handleLoadIntersection = useCallback(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !loadMedia) {
          setLoadMedia(true);
        }
      }
    },
    [loadMedia]
  );

  const handlePlayIntersection = useCallback(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !playMedia) {
          setPlayMedia(true);
        } else if (!entry.isIntersecting && playMedia) {
          setPlayMedia(false);
        }
      }
    },
    [playMedia]
  );

  const loadComplete = () => {
    setLoaded(true);
  };

  // `playing` fires when the element *begins* playback, which is not the same
  // thing as a frame having been composited — on a cold HLS start there is a
  // window where the video is live but has painted nothing, which showed up as a
  // one-frame black flash the moment the poster stopped covering it. Where
  // requestVideoFrameCallback exists it owns this flag instead (see below), so
  // `playing` only has to stand in on Firefox, which has no rVFC.
  const handlePlaying = () => {
    if (supportsFrameCallback(mediaRef.current)) return;
    setIsPlaying(true);
  };

  useEffect(() => {
    if (!mediaRef.current || !loaded) return;
    if (touch && !touchFired) return;

    if (playMedia) {
      const playPromise = mediaRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    } else {
      mediaRef.current.pause();
    }
  }, [playMedia, loaded, touch, touchFired]);

  useEffect(() => {
    if (!boxRef.current) return;

    loadObserverRef.current = new IntersectionObserver(handleLoadIntersection, {
      root: null,
      rootMargin: '0px 0px 100% 0px',
      threshold: 0,
    });
    loadObserverRef.current.observe(boxRef.current);

    playObserverRef.current = new IntersectionObserver(handlePlayIntersection, {
      root: null,
      rootMargin: '100px 0px 100px 0px',
      threshold: 0,
    });
    playObserverRef.current.observe(boxRef.current);

    return () => {
      if (loadObserverRef.current) loadObserverRef.current.disconnect();
      if (playObserverRef.current) playObserverRef.current.disconnect();
    };
  }, [handleLoadIntersection, handlePlayIntersection]);

  useEffect(() => {
    setLoaded(false);
    setIsPlaying(false);
  }, [activeSrc]);

  // Reveal the video on the first frame actually presented for compositing, not
  // on the `playing` event. One-shot: once a frame has landed the element is
  // opaque for the rest of its life, and pausing out of view doesn't undo that.
  useEffect(() => {
    const video = mediaRef.current;
    if (!video || !loadMedia || !supportsFrameCallback(video)) return;

    const handle = video.requestVideoFrameCallback(() => setIsPlaying(true));

    return () => video.cancelVideoFrameCallback?.(handle);
  }, [loadMedia, activeSrc]);

  // Hooks must run unconditionally; the result is only used when parallax is on.
  const { scrollYProgress } = useScroll({
    target: parentRef ? parentRef : boxRef,
    offset: ['start end', 'end start'],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  if (!media) return null;

  const width = media.width || 1920;
  const height = media.height || 1080;

  let figureStyle = {};
  // The video sits IN FRONT of the poster, and hides itself until it has a real
  // frame to show. That ordering is what removes the black flash: a media element
  // with nothing decoded yet paints an opaque black box in some engines, so
  // leaving it visible underneath a poster that has just been taken away is
  // exactly the frame that flashed. Held at opacity 0 it can't contribute
  // anything, the poster behind it shows through, and the handover happens by
  // the video becoming opaque over the top rather than by the poster leaving.
  let mediaStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    overflow: 'hidden',
    left: '0px',
    top: '0px',
    objectFit: 'cover',
    opacity: isPlaying ? 1 : 0,
  };
  // Flat colour covering the video while it loads, for the no-poster case
  // (VideoBackground). Not rendered when there is a poster: a poster covers the
  // same gap with something better, and fading a dark wash out over an
  // already-visible video is worse than not drawing it at all.
  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'var(--color-midnight-slate)',
    opacity: isPlaying ? 0 : 1,
    transition: 'opacity 1.6s ease',
    pointerEvents: 'none',
  };

  // Poster sits BEHIND the video and is authored as the video's own first frame,
  // so there is no visible cut to make: the video simply becomes opaque over the
  // top of an identical image. It is then faded out — after a delay, and only
  // once the video is definitely painting — purely so it stops costing a
  // composited layer. The overlap is deliberate slack: nothing is riding on the
  // exact frame the poster leaves on, because by then it is completely covered.
  //
  // If autoplay is blocked (Low Power Mode, data-saver, perf throttling) no frame
  // is ever presented, so this never runs and the poster stays as the static
  // fallback with the transparent video on top of it.
  const posterStyle = {
    position: 'absolute',
    inset: 0,
    opacity: isPlaying ? 0 : 1,
    transition: 'opacity 400ms ease 300ms',
    pointerEvents: 'none',
  };

  if (fill) {
    figureStyle = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      overflow: 'hidden',
      top: '0px',
      left: '0px',
    };
    if (forceHeight) figureStyle.height = `${height}px`;
  } else if (forceHeight) {
    figureStyle = {
      position: 'relative',
      width: '100%',
      height: 'auto',
      paddingTop: `${(height / width) * 100}%`,
    };
  }

  const videoElement = (
    <video
      className={`${loaded ? 'load' : ''}`}
      ref={mediaRef}
      onCanPlay={loadComplete}
      onPlaying={handlePlaying}
      src={activeIsM3U8 ? undefined : activeSrc}
      style={mediaStyle}
      loop={!activeIsM3U8} // ✅ ONLY loop MP4
      muted
      playsInline
      preload="auto"
      autoPlay
    />
  );

  const posterElement = poster ? (
    <div style={posterStyle}>
      <CustomImage
        image={poster}
        fill
        fillMedia
        forceLoad={forceLoad}
        fetchPriority={fetchPriority}
        quality={90}
      />
    </div>
  ) : null;

  // Poster and video share ONE transform frame, and it is load-bearing. The
  // parallax frame is 110% tall, offset -5% and translated by `parallaxY`; a
  // poster sitting at the figure's own inset:0 is 100% tall and still, so
  // `object-fit: cover` crops the two differently. The handover works by the
  // video becoming opaque directly over the poster, so any mismatch between the
  // two shows as a jump at that exact moment — the old 1.6s crossfade was what
  // used to hide it. Rendering both inside the same frame makes them
  // pixel-identical at every scroll position.
  //
  // Plain div when parallax is off: this fragment renders on every post card, and
  // a motion component per card for a transform that never changes isn't free.
  const Frame = parallax ? motion.div : 'div';
  const frameStyle = parallax
    ? {
        position: 'absolute',
        height: '110%',
        width: '100%',
        top: '-5%',
        y: parallaxY,
      }
    : { position: 'absolute', inset: 0 };

  return (
    <figure
      className={`${className} ${loaded ? 'is-loaded' : ''}`}
      ref={boxRef}
      style={figureStyle}
    >
      <Frame style={frameStyle}>
        {posterElement}
        {loadMedia && videoElement}
        {loadMedia && !poster && <div style={overlayStyle} />}
      </Frame>
    </figure>
  );
}
